import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { cloneDeep } from "lodash";
import { OrganisationContact } from "../../organisation/objects/organisationContact";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_SUB_NETWORK, EMPTY_NETWORK_LIST_CONFIGURATION, EMPTY_SUB_NETWORK_LIST_CRITERIA, EMPTY_SUB_NETWORK_MARGIN_LIST_CONFIGURATION, EMPTY_SUB_NETWORK_MARGIN_LIST_CRITERIA, SUB_NETWORK_MARGIN_MODULE, SUB_NETWORK_MODULE } from "../constant/subNetworkConstant";
import { SubNetworkListCriteria } from "../objects/subNetworkListCriteria";
import { SubNetworkState } from "../objects/subNetworkstate";
import { setNetworkPreference } from "../utils/setNetworkPreference";
import { Margin } from "../../margin/objects/margin";
import { setMarginPreference } from "../utils/setMarginPreference";
import { MarginListCriteria } from "../objects/marginListCriteria";
import { Column } from "../objects/column";
import { Network } from "../../network/objects/network";


const initialState: SubNetworkState = {
    sub_network_list: [],
    sub_network_list_configuration: EMPTY_NETWORK_LIST_CONFIGURATION,
    sub_network_list_loading: false,
    sub_network_list_count: 0,
    sub_network_list_criteria: EMPTY_SUB_NETWORK_LIST_CRITERIA,
    sub_network: EMPTY_SUB_NETWORK,
    margins: [],
    margins_configuration: EMPTY_SUB_NETWORK_MARGIN_LIST_CONFIGURATION,
    margin: null,
    margins_loading: false,
    margins_count: 0,
    margins_criteria: EMPTY_SUB_NETWORK_MARGIN_LIST_CRITERIA,
    suspended_margin: null,
    first_preference_loaded: false,
    payment_policy: null,
    currencies: [],
    payment_details_view: null
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setSuspendedMarginReducer = (state: SubNetworkState, action: PayloadAction<Margin | null>): SubNetworkState => ({
    ...state, suspended_margin: action.payload
});

const addMarginsConfigurationPreferenceReducer = (state: SubNetworkState, action: PayloadAction<Preferences[]>): SubNetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: [...state.margins_configuration.preferences, ...action.payload] }
});

const setMarginsConfigurationPreferenceReducer = (state: SubNetworkState, action: PayloadAction<Preferences[]>): SubNetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: action.payload }
});

const toggleColumnMarginsReducer = (state: SubNetworkState, action: PayloadAction<number>): SubNetworkState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const setSubNetworkMarginsReducer = (state: SubNetworkState, action: PayloadAction<Margin[]>): SubNetworkState => ({
    ...state, margins: action.payload
});
const updateMarginsCriteriaReducer = (state: SubNetworkState, action: PayloadAction<MarginListCriteria>): SubNetworkState => ({
    ...state, margins_criteria: action.payload
});
const updateMarginReducer = (state: SubNetworkState, action: PayloadAction<Margin | null>): SubNetworkState => ({
    ...state, margin: action.payload
});
const updateMarginsLoadingReducer = (state: SubNetworkState, action: PayloadAction<boolean>): SubNetworkState => ({
    ...state, margins_loading: action.payload
});
const updateMarginsCountReducer = (state: SubNetworkState, action: PayloadAction<number>): SubNetworkState => ({
    ...state, margins_count: action.payload
});

const addSubNetworkListConfigurationPreferenceReducer = (state: SubNetworkState, action: PayloadAction<Preferences[]>): SubNetworkState => ({
    ...state, sub_network_list_configuration: { ...state.sub_network_list_configuration, preferences: [...state.sub_network_list_configuration.preferences, ...action.payload] }
});

const setSubNetworkListConfigurationPreferenceReducer = (state: SubNetworkState, action: PayloadAction<Preferences[]>): SubNetworkState => ({
    ...state, sub_network_list_configuration: { ...state.sub_network_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: SubNetworkState, action: PayloadAction<Preferences[]>): SubNetworkState => {
    let main_preference = {};
    if(action.payload.find(value => value.module === SUB_NETWORK_MODULE)) {
        const { preferences, columns, order_by, order } = setNetworkPreference(action.payload, state.sub_network_list_configuration.columns);
        main_preference = {
            sub_network_list_configuration: { ...state.sub_network_list_configuration, preferences, columns },
            sub_network_list_criteria: { ...state.sub_network_list_criteria, order, order_by },
        };
    }
    let margin_pref = {};
    if(action.payload.find(value => value.module === SUB_NETWORK_MARGIN_MODULE)) {
        const { preferences, columns, order_by, order } = setMarginPreference(action.payload, state.margins_configuration.columns);
        margin_pref = {
            margins_configuration: { ...state.margins_configuration, preferences: preferences, columns: columns },
            margins_criteria: { ...state.margins_criteria, order: order, order_by: order_by }
        };
    }
    return {
        ...state,
        ...main_preference,
        ...margin_pref,
        first_preference_loaded: true
    };
};

const toggleColumnReducer = (state: SubNetworkState, action: PayloadAction<number>): SubNetworkState => {
    const columns = cloneDeep([...state.sub_network_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, sub_network_list_configuration: { ...state.sub_network_list_configuration, columns } };
};

const updateSubNetworkListReducer = (state: SubNetworkState, action: PayloadAction<Network[]>): SubNetworkState => ({
    ...state, sub_network_list: action.payload
});
const updateSubNetworkListCriteriaReducer = (state: SubNetworkState, action: PayloadAction<SubNetworkListCriteria>): SubNetworkState => ({
    ...state, sub_network_list_criteria: action.payload
});
const updateSubNetworkReducer = (state: SubNetworkState, action: PayloadAction<Network>): SubNetworkState => ({
    ...state, sub_network: action.payload
});
const updateSubNetworkListLoadingReducer = (state: SubNetworkState, action: PayloadAction<boolean>): SubNetworkState => ({
    ...state, sub_network_list_loading: action.payload
});
const updateSubNetworkListCountReducer = (state: SubNetworkState, action: PayloadAction<number>): SubNetworkState => ({
    ...state, sub_network_list_count: action.payload
});

const setColumnsReducer = (state: SubNetworkState, action: PayloadAction<Column[]>): SubNetworkState => ({
    ...state, sub_network_list_configuration: { ...state.sub_network_list_configuration, columns: action.payload },
});

const setMarginColumnsReducer = (state: SubNetworkState, action: PayloadAction<Column[]>): SubNetworkState => ({
    ...state, margins_configuration: { ...state.margins_configuration, columns: action.payload },
});

const setSubNetworkPaymentPolicyReducer = (state: SubNetworkState, action: PayloadAction<any>): SubNetworkState => ({
    ...state, payment_policy: action.payload
});

const updateSubNetworkPaymentPolicyReducer = (state: SubNetworkState, action: PayloadAction<any>): SubNetworkState => ({
    ...state, payment_policy: action.payload
});

const setSubNetworkCurrenciesReducer = (state: SubNetworkState, action: PayloadAction<any>): SubNetworkState => ({
    ...state, currencies: action.payload
});

const setSubNetworkPaymentDetailsViewReducer = (state: SubNetworkState, action: PayloadAction<'credit_line' | 'wallet' | 'onetime_overflow' | null>): SubNetworkState => ({
    ...state, payment_details_view: action.payload
});

export const sub_network = createSlice({
    name: 'sub_network',
    initialState,
    reducers: {
        setSuspendedMargin: setSuspendedMarginReducer,
        addMarginsConfigurationPreference: addMarginsConfigurationPreferenceReducer,
        setMarginsConfigurationPreference: setMarginsConfigurationPreferenceReducer,
        toggleColumnMargins: toggleColumnMarginsReducer,
        setSubNetworkMargins: setSubNetworkMarginsReducer,
        updateMarginsCriteria: updateMarginsCriteriaReducer,
        updateMargin: updateMarginReducer,
        updateMarginsLoading: updateMarginsLoadingReducer,
        updateMarginsCount: updateMarginsCountReducer,
        addSubNetworkListConfigurationPreference: addSubNetworkListConfigurationPreferenceReducer,
        setSubNetworkListConfigurationPreference: setSubNetworkListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateSubNetworkList: updateSubNetworkListReducer,
        updateSubNetworkListCriteria: updateSubNetworkListCriteriaReducer,
        updateSubNetwork: updateSubNetworkReducer,
        updateSubNetworkListLoading: updateSubNetworkListLoadingReducer,
        updateSubNetworkListCount: updateSubNetworkListCountReducer,
        setColumns: setColumnsReducer,
        setMarginColumns: setMarginColumnsReducer,
        setSubNetworkPaymentPolicy: setSubNetworkPaymentPolicyReducer,
        updateSubNetworkPaymentPolicy: updateSubNetworkPaymentPolicyReducer,
        setSubNetworkCurrencies: setSubNetworkCurrenciesReducer,
        setSubNetworkPaymentDetailsView: setSubNetworkPaymentDetailsViewReducer,
        resizeColumn: (state: SubNetworkState, action: PayloadAction<ColumnResizePayload>): SubNetworkState => ({
            ...state, sub_network_list_configuration: { ...state.sub_network_list_configuration, columns: applyColumnResize(state.sub_network_list_configuration.columns, action.payload) }
        }),
        resizeMarginsColumn: (state: SubNetworkState, action: PayloadAction<ColumnResizePayload>): SubNetworkState => ({
            ...state, margins_configuration: { ...state.margins_configuration, columns: applyColumnResize(state.margins_configuration.columns, action.payload) }
        }),
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setSuspendedMargin,
    addMarginsConfigurationPreference,
    setMarginsConfigurationPreference,
    setColumns,
    toggleColumnMargins,
    setSubNetworkMargins,
    updateMarginsCriteria,
    updateMargin,
    updateMarginsLoading,
    updateMarginsCount,
    addSubNetworkListConfigurationPreference,
    setSubNetworkListConfigurationPreference,
    toggleColumn,
    updateSubNetworkList,
    updateSubNetworkListCriteria,
    updateSubNetwork,
    updateSubNetworkListLoading,
    updateSubNetworkListCount,
    setMarginColumns,
    setSubNetworkPaymentPolicy,
    updateSubNetworkPaymentPolicy,
    setSubNetworkCurrencies,
    setSubNetworkPaymentDetailsView,
    resizeColumn,
    resizeMarginsColumn,
} = sub_network.actions;
